import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Users, X, Sparkles, Upload, FolderOpen, Clapperboard, Check, Info } from 'lucide-react';
import { SyncBadge } from './SyncBadge';

const errText = (err, fallback) => { const d = err.response?.data?.detail; return typeof d === 'string' ? (d.startsWith('MISSING_KEY:') ? d.split('|')[1] : d) : fallback; };

// Workspace-level detail for a character or location: image, editable description, regenerate/upload, scenes using it
export function WorkspaceEntityDetail({ kind, item, onClose, onUpdated }) {
  const isChar = kind === 'character';
  const navigate = useNavigate();
  const [entity, setEntity] = useState(item);
  const [name, setName] = useState(item.name || '');
  const [desc, setDesc] = useState(item.description || '');
  const [provider, setProvider] = useState('gemini');
  const [busy, setBusy] = useState(null);
  const [scenes, setScenes] = useState(null);
  const [tab, setTab] = useState('info');
  const uploadRef = useRef(null);
  const base = `/projects/${entity.project_id}/${isChar ? 'characters' : 'locations'}/${entity.id}`;
  const imagePath = isChar ? entity.image_storage_path : entity.image_path;

  useEffect(() => {
    api.get(`/projects/${entity.project_id}/scenes`).then(r => {
      const all = Array.isArray(r.data) ? r.data : [];
      setScenes(all.filter(s => isChar ? (s.character_ids || []).includes(entity.id) : (s.location_id === entity.id || s.closing_location_id === entity.id)));
    }).catch(() => setScenes([]));
  }, [entity.project_id, entity.id, isChar]);

  const apply = (updated) => { const merged = { ...entity, ...updated }; if (!merged.sync_group) delete merged.identity_members; setEntity(merged); onUpdated?.(merged); };
  const dirty = name.trim() !== (entity.name || '') || desc.trim() !== (entity.description || '');

  const save = async () => {
    setBusy('save');
    try { const res = await api.put(base, { name: name.trim(), description: desc.trim() }); apply(res.data); toast.success('Saved'); }
    catch (err) { toast.error(errText(err, 'Save failed')); }
    finally { setBusy(null); }
  };

  const regenerate = async () => {
    setBusy('gen');
    try {
      if (dirty) { const res = await api.put(base, { name: name.trim(), description: desc.trim() }); apply(res.data); }
      if (isChar) {
        await api.post(`${base}/generate-image`, { prompt: '', image_provider: provider }, { timeout: 15000 });
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 3000));
          const st = await api.get(`${base}/image-gen-status`, { params: { _t: Date.now() } });
          if (st.data.status === 'complete' && st.data.character) { apply(st.data.character); toast.success('Character image regenerated'); return; }
          if (st.data.status === 'failed') { toast.error(st.data.error || 'Image generation failed'); return; }
        }
        toast.error('Image generation timed out');
      } else {
        const res = await api.post(`${base}/generate-image`, { description: desc.trim(), image_provider: provider }, { timeout: 180000 });
        apply(res.data); toast.success('Location image regenerated');
      }
    } catch (err) { toast.error(errText(err, 'Image generation failed')); }
    finally { setBusy(null); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy('upload');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`${base}/upload-image`, fd);
      apply(res.data.character || res.data); toast.success('Image uploaded');
    } catch (err) { toast.error(errText(err, 'Upload failed')); }
    finally { setBusy(null); if (uploadRef.current) uploadRef.current.value = ''; }
  };

  const Icon = isChar ? Users : MapPin;
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-4" data-testid={`workspace-detail-${kind}`} onClick={onClose}>
      <div className="w-full max-w-4xl bg-[#0E0E12] border border-white/10 rounded-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#8A8A93] flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-[#FF4500]" /> {isChar ? 'Character' : 'Location'} · <FolderOpen className="w-3 h-3" /> {entity.project_name}</p>
          <div className="flex items-center gap-3">
            <div className="flex gap-0.5 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]" data-testid="workspace-detail-tabs">
              {[{ id: 'info', label: 'Info', icon: Info }, { id: 'scenes', label: `Scenes${scenes ? ` (${scenes.length})` : ''}`, icon: Clapperboard }].map(t => (
                <button key={t.id} data-testid={`workspace-detail-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${tab === t.id ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93] hover:text-white'}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <button data-testid="workspace-detail-close" onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-0 overflow-y-auto">
          <div className="bg-[#0a0a0e] relative min-h-[260px] flex items-center justify-center">
            {imagePath ? <img data-testid="workspace-detail-image" src={getImageUrl(imagePath)} alt={entity.name} className="w-full h-full object-cover max-h-[70vh]" /> : <Icon className="w-12 h-12 text-[#55555C]" />}
            {busy === 'gen' && <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2 text-xs text-white"><Loader2 className="w-6 h-6 animate-spin" /> Generating image…</div>}
          </div>
          <div className="p-5 space-y-4">
            {tab === 'info' ? (<>
            {entity.sync_group && (
              <div className="p-2.5 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20 space-y-2" data-testid="workspace-detail-sync-row">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-[#8A8A93]"><span className="text-[#10B981] font-semibold">One identity across projects</span> — look, voice and tags stay identical everywhere.</p>
                  <SyncBadge kind={kind} entity={entity} onDetached={apply} />
                </div>
                {entity.identity_members?.length > 1 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="workspace-detail-identity-projects">
                    {entity.identity_members.map(m => (
                      <span key={m.id} className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${m.id === entity.id ? 'border-[#10B981]/40 text-[#10B981] bg-[#10B981]/10' : 'border-white/10 text-[#c8c8d0]'}`}>
                        <FolderOpen className="w-3 h-3" /> {m.project_name}{m.id === entity.id ? ' · primary' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Name</label>
              <input data-testid="workspace-detail-name" value={name} onChange={e => setName(e.target.value)} className="ff-input w-full text-sm font-semibold mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Description</label>
              <textarea data-testid="workspace-detail-desc" value={desc} onChange={e => setDesc(e.target.value)} rows={6} className="ff-input w-full text-xs mt-1 resize-none" placeholder={isChar ? 'Appearance, age, wardrobe…' : 'Architecture, light, weather, era…'} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="workspace-detail-save" onClick={save} disabled={!dirty || !!busy} className="ff-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40">{busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save</button>
              <div className="flex gap-0.5 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                {[{ id: 'gemini', l: 'Gemini' }, { id: 'openai', l: 'GPT Image' }].map(p => (
                  <button key={p.id} onClick={() => setProvider(p.id)} className={`px-2 py-1 rounded-md text-[10px] ${provider === p.id ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93]'}`}>{p.l}</button>
                ))}
              </div>
              <button data-testid="workspace-detail-regen" onClick={regenerate} disabled={!!busy || !desc.trim()} className="text-xs px-3 py-2 rounded-lg bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 hover:bg-[#10B981]/25 disabled:opacity-40 flex items-center gap-1.5">
                {busy === 'gen' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {imagePath ? 'Regenerate image' : 'Generate image'}
              </button>
              <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="workspace-detail-upload-input" />
              <button data-testid="workspace-detail-upload" onClick={() => uploadRef.current?.click()} disabled={!!busy} className="text-xs px-3 py-2 rounded-lg bg-white/[0.04] text-[#8A8A93] border border-white/[0.06] hover:text-white disabled:opacity-40 flex items-center gap-1.5">
                {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
              </button>
            </div>
            </>) : (
            <div data-testid="workspace-detail-scenes-tab">
              <p className="text-[10px] uppercase tracking-wider text-[#8A8A93] mb-2 flex items-center gap-1.5"><Clapperboard className="w-3 h-3" /> Scenes {isChar ? 'featuring' : 'using'} {entity.name} {scenes && <span className="text-[#55555C]">({scenes.length})</span>}</p>
              {scenes === null ? <Loader2 className="w-4 h-4 animate-spin text-[#8A8A93]" />
                : scenes.length === 0 ? <p className="text-xs text-[#55555C]">Not used in any scene yet.</p>
                : (
                  <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1" data-testid="workspace-detail-scenes">
                    {scenes.map(s => (
                      <button key={s.id} data-testid={`workspace-detail-scene-${s.id}`} onClick={() => navigate(`/project/${entity.project_id}/scenes/${s.id}`)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg bg-[#141418] border border-white/[0.06] hover:border-[#FF4500]/40 text-left">
                        <div className="w-16 aspect-video rounded bg-[#0a0a0e] overflow-hidden shrink-0">{s.first_frame_path && <img src={getImageUrl(s.first_frame_path)} alt="" className="w-full h-full object-cover" />}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-white truncate">{(s.scene_order || 0) + 1}. {s.title}</p>
                          <p className="text-[10px] text-[#55555C] truncate">{s.setting || s.scene_prompt || ''}</p>
                        </div>
                        {s.video_path && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">video</span>}
                      </button>
                    ))}
                  </div>
                )}
            </div>
            )}
            <button data-testid="workspace-detail-open-project" onClick={() => navigate(`/project/${entity.project_id}?tab=${isChar ? 'characters' : 'locations'}`)} className="text-xs text-[#FF4500] hover:text-[#FF5722] flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> Open in project</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
